import { forwardRef, useImperativeHandle, useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CommonRichTextField from '../../../../../Components/CommonRichTextField/CommonRichTextField';
import * as action from '../../../store/DevelopmentOrder.action';

/**
 * Internal Comments editor (BM-8848). The legacy admin/supplier speech-bubble feed
 * has been migrated into a single editable `internalComment` rich-text field on the
 * Sample doc, so this component now renders only that one editor. New live
 * communication is handled by the Chat module — there is no composer/add/edit/
 * delete flow here any more.
 *
 * Binds `sampleDetails.internalComment` and persists through `saveSampleTabData`
 * (section `internalComment`), mirroring the Licensing/Customer comment columns.
 * Autosaves on unmount and on `beforeunload`; the parent Save button flushes via
 * the imperative `submitComment()` handle.
 */
const CommonComment = forwardRef(({ SampleAddEditAccess, isViewingPreviousVersion }, ref) => {
  const { t } = useTranslation('common');
  const queryParams = useLocation();
  const { search } = queryParams;
  const smapleId = new URLSearchParams(search).get('sample');
  const dispatch = useDispatch();
  const { devOrderId } = useParams();

  const {
    UserDetails: { isSupplierLoggedIn },
    Measuremets: { sampleDetails },
    BOMConfigDetails: { isProductArchived },
  } = useSelector((prev) => prev);

  /** Suppliers, archived products, previous versions and users without sample edit rights are read-only — no editing, no Save. */
  const isEditorDisabled = isProductArchived || isSupplierLoggedIn || isViewingPreviousVersion || !SampleAddEditAccess;

  const storedValue = sampleDetails?.internalComment ?? '';
  const [html, setHtml] = useState(storedValue);
  const currentRef = useRef(storedValue);
  const savedRef = useRef(storedValue);

  useEffect(() => {
    setHtml(storedValue);
    currentRef.current = storedValue;
    savedRef.current = storedValue;
  }, [storedValue]);

  const handleChange = useCallback((data) => {
    currentRef.current = data;
    setHtml(data);
  }, []);

  /**
   * Persists the current value when it changed. `silent` suppresses the success
   * toast — used by blur/unmount autosave so clicking away does not spam toasts;
   * the explicit parent Save shows the toast.
   *
   * @param {boolean} [silent=true] - When true, no success toaster is shown.
   * @returns {void}
   */
  const persist = useCallback(
    (silent = true) => {
      if (isEditorDisabled || currentRef.current === savedRef.current) {
        return;
      }
      const nextValue = currentRef.current;
      savedRef.current = nextValue;
      const successMessage = silent ? undefined : t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.COMMENT_COLUMNS.INTERNAL_SAVED', { ns: 'product-sample' });
      dispatch(
        action.saveSampleTabData(
          { internalComment: nextValue },
          { devOrderId, section: 'internalComment', sampleId: smapleId },
          silent ? 'tosterOff' : undefined,
          successMessage,
        ),
      );
    },
    [dispatch, devOrderId, smapleId, isEditorDisabled, t],
  );

  const autoSave = useCallback(() => persist(true), [persist]);

  useEffect(() => () => autoSave(), [autoSave]);

  useEffect(() => {
    window.addEventListener('beforeunload', autoSave);
    return () => window.removeEventListener('beforeunload', autoSave);
  }, [autoSave]);

  useImperativeHandle(ref, () => ({
    submitComment() {
      persist(false);
      return true;
    },
    hasPendingChanges() {
      return !isEditorDisabled && currentRef.current !== savedRef.current;
    },
  }));

  return (
    <div className={`${!SampleAddEditAccess ? 'disabled-section' : ''}`}>
      <p className="sample-comment-column__title">{t(`DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.COMMENT_COLUMNS.${isSupplierLoggedIn ? 'INTERNAL_TITLE' : 'PRIMARY_TITLE'}`, { ns: 'product-sample' })}</p>
      <CommonRichTextField
        label=""
        value={html}
        onChange={handleChange}
        disabled={isEditorDisabled}
        minHeight="calc(100vh - 350px)"
      />
    </div>
  );
});

CommonComment.displayName = 'CommonComment';
export default CommonComment;
